/**
 * AuthApi - HTTP transport layer for authentication
 *
 * Consolidates logic from:
 * - contexts/authMe.ts
 * - contexts/authRegister.ts
 * - contexts/authVerify.ts
 *
 * RULES:
 * - NO state management (that's the store's job)
 * - NO caching (use store or React Query if needed)
 * - Pure HTTP calls with typed inputs/outputs
 * - Injected via constructor (Dependency Injection)
 *
 * Usage: Never instantiate directly. Access via RootStore:
 * this.root.api.auth.authMe(token)
 */

import { ApiClient, ApiError } from "@/shared/api/baseClient";
import type {
  AuthMeResponse,
  AuthMeErrorCode,
  AuthMeError,
  RegisterRequest,
  RegisterResponse,
  RegisterApiError,
  SendVerificationResponse,
  ResendVerificationResponse,
} from "../types/auth.types";

export class AuthApi {
  constructor(private client: ApiClient) {}

  // =============================================
  // Auth/Me - Authorization Check
  // =============================================

  /**
   * Check user authorization status
   * Called after Firebase authentication to verify backend permissions
   *
   * @param token - Firebase ID token
   * @returns User authorization details
   * @throws AuthMeError with specific error codes
   */
  async authMe(token: string): Promise<AuthMeResponse> {
    try {
      this.client.setAuthToken(token);
      return await this.client.get<AuthMeResponse>("/auth/me", undefined, {});
    } catch (e) {
      // Keep backward-compatible error codes for existing auth flow
      if (e instanceof ApiError) {
        const code = e.code as AuthMeErrorCode;
        if (
          code === "NOT_VERIFIED" ||
          code === "FORBIDDEN" ||
          code === "UNAUTHENTICATED" ||
          code === "NEEDS_REGISTRATION"
        ) {
          const err = new Error(code) as AuthMeError;
          err.code = code;
          err.httpStatus = e.httpStatus;
          throw err;
        }
      }
      throw e;
    }
  }

  // =============================================
  // Auth/Register - User Registration
  // =============================================

  /**
   * Register a new user or partner account
   * Called after Firebase signup to create backend user record
   *
   * @param token - Firebase ID token
   * @param payload - Registration details
   * @returns Registration response with org_id for partners
   * @throws RegisterApiError
   */
  async register(
    token: string,
    payload: RegisterRequest,
  ): Promise<RegisterResponse> {
    try {
      this.client.setAuthToken(token);
      return await this.client.post<RegisterResponse>(
        "/auth/register",
        payload,
        { defaultErrorCode: "REGISTER_FAILED" },
      );
    } catch (e) {
      if (e instanceof ApiError) {
        const err = new Error(e.code) as RegisterApiError;
        err.code = e.code;
        err.httpStatus = e.httpStatus;
        throw err;
      }
      throw e;
    }
  }

  // =============================================
  // Auth/Verify - Email Verification
  // =============================================

  /**
   * Send verification email for authenticated user
   * Called during registration flow when user has a valid token
   *
   * @param token - Firebase ID token
   * @returns Verification response with optional link (emulator only)
   */
  async sendEmailVerification(
    token: string,
  ): Promise<SendVerificationResponse> {
    this.client.setAuthToken(token);
    const result = await this.client.post<SendVerificationResponse>(
      "/auth/email-verification/send",
      {},
      { defaultErrorCode: "VERIFY_EMAIL_SEND_FAILED" },
    );

    // Mark that we're awaiting verification so VerifyEmailPage can
    // distinguish between "came from email redirect" vs "navigated directly"
    // Use localStorage (not sessionStorage) because email links open in new tabs
    if (result.ok) {
      try {
        localStorage.setItem("awaitingVerification", "true");
      } catch {
        // Ignore storage errors
      }
    }

    return result;
  }

  /**
   * Resend verification email by email address
   * Called when a user needs to request a new verification link
   * (e.g., after the original link expired)
   *
   * Note: This is an unauthenticated endpoint since the user cannot log in
   * without verifying their email first
   *
   * @param email - User's email address
   * @returns Resend response
   */
  async resendEmailVerification(
    email: string,
  ): Promise<ResendVerificationResponse> {
    // Use a fresh client without auth token for this unauthenticated endpoint
    const unauthClient = new ApiClient();
    const result = await unauthClient.post<ResendVerificationResponse>(
      "/auth/email-verification/resend",
      { email },
      { defaultErrorCode: "RESEND_EMAIL_FAILED" },
    );

    // Mark that we're awaiting verification (same as initial send)
    // Use localStorage (not sessionStorage) because email links open in new tabs
    if (result.ok) {
      try {
        localStorage.setItem("awaitingVerification", "true");
      } catch {
        // Ignore storage errors
      }
    }

    return result;
  }
}
